import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { IAdminUser, addTeamMembers, invalidateTeamQueries, useAdminUsersQuery } from '../../api/admin';
import { apiErrorMessage } from '../../utils/api';

interface Props {
	open: boolean;
	onClose: () => void;
	teamId: number;
	teamName: string;
	/** Users already on the team — excluded from the picker. */
	existingMemberIds: number[];
}

export default function AddTeamMembersDialog({ open, onClose, teamId, teamName, existingMemberIds }: Props) {
	const queryClient = useQueryClient();
	// the users list is the heaviest query in the app; only load it once the picker is open
	const { data: allUsers, isLoading: usersLoading } = useAdminUsersQuery({ enabled: open });

	const [selectedUsers, setSelectedUsers] = useState<IAdminUser[]>([]);
	const [canShare, setCanShare] = useState(true);
	const [canReceive, setCanReceive] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [addedCount, setAddedCount] = useState<number | null>(null);

	const eligibleUsers = (allUsers ?? []).filter((u) => u.is_active && !existingMemberIds.includes(u.id));

	function resetAndClose() {
		setSelectedUsers([]);
		setCanShare(true);
		setCanReceive(true);
		setError(null);
		setAddedCount(null);
		onClose();
	}

	async function handleSubmit() {
		if (selectedUsers.length === 0) return;
		setSubmitting(true);
		setError(null);
		try {
			await addTeamMembers(teamId, {
				user_ids: selectedUsers.map((u) => u.id),
				can_share: canShare,
				can_receive: canReceive,
			});
			await invalidateTeamQueries(queryClient);
			setAddedCount(selectedUsers.length);
		} catch (e: any) {
			setError(apiErrorMessage(e, 'An unexpected error occurred. Please try again.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Dialog
			open={open}
			onClose={resetAndClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{ sx: { backgroundImage: 'none' } }}
		>
			{addedCount !== null ? (
				<>
					<DialogTitle>Users Added</DialogTitle>
					<DialogContent>
						<Alert severity="success">
							{addedCount} user{addedCount !== 1 ? 's' : ''} added to <strong>{teamName}</strong>.
						</Alert>
					</DialogContent>
					<DialogActions>
						<Button variant="contained" onClick={resetAndClose}>
							Done
						</Button>
					</DialogActions>
				</>
			) : (
				<>
					<DialogTitle>Add Users to {teamName}</DialogTitle>
					<DialogContent sx={{ pb: 1 }}>
						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							Select one or more users to add to this team. Users already on the team are not listed.
						</Typography>
						<Autocomplete
							multiple
							options={eligibleUsers}
							loading={usersLoading}
							disableCloseOnSelect
							filterSelectedOptions
							value={selectedUsers}
							onChange={(_, value) => setSelectedUsers(value)}
							getOptionLabel={(u) => (u.full_name ? `${u.full_name} (${u.email})` : u.email)}
							isOptionEqualToValue={(a, b) => a.id === b.id}
							renderTags={(value, getTagProps) =>
								value.map((u, index) => (
									<Chip {...getTagProps({ index })} label={u.full_name ?? u.email} size="small" />
								))
							}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Users"
									placeholder="Search by name or email…"
									size="small"
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{usersLoading && <CircularProgress color="inherit" size={16} />}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
						/>
						<Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
							<FormControlLabel
								control={
									<Switch size="small" checked={canShare} onChange={(e) => setCanShare(e.target.checked)} />
								}
								label={<Typography variant="body2">Can share</Typography>}
							/>
							<FormControlLabel
								control={
									<Switch size="small" checked={canReceive} onChange={(e) => setCanReceive(e.target.checked)} />
								}
								label={<Typography variant="body2">Can receive</Typography>}
							/>
						</Box>
						<Typography variant="caption" color="text.secondary">
							Controls whether these users can share resources to, and receive resources from, their
							teammates on the platform.
						</Typography>
					</DialogContent>
					<DialogActions>
						<Button onClick={resetAndClose} disabled={submitting}>
							Cancel
						</Button>
						<Button
							variant="contained"
							onClick={handleSubmit}
							disabled={submitting || selectedUsers.length === 0}
							startIcon={submitting ? <CircularProgress size={16} /> : undefined}
						>
							{submitting
								? 'Adding…'
								: `Add ${selectedUsers.length} User${selectedUsers.length !== 1 ? 's' : ''}`}
						</Button>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
}
